import { useState, useEffect } from "react";
import { Plus, LogIn, Users, Copy, Trash2, LogOut, Crown, Shield, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { apiCreateLeague, apiJoinLeague, apiGetMyLeagues, apiGetLeague, apiLeaveLeague, apiRemoveMember, getUserId } from "@/lib/storage";
import { toast } from "sonner";

export default function Leagues() {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const userId = getUserId();

  const refresh = async () => {
    setLoading(true);
    const data = await apiGetMyLeagues();
    setLeagues(data);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const openDetail = async (league) => {
    const full = await apiGetLeague(league.id);
    if (full) setSelectedLeague(full);
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="leagues-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>FANTASY LEAGUES</h1>
        <div className="flex gap-2">
          <button data-testid="refresh-leagues-btn" onClick={refresh}
            className="p-1.5 hover:bg-white/5 rounded text-slate-500 hover:text-white transition-colors">
            <RefreshCw size={14} />
          </button>
          <button data-testid="join-league-btn" onClick={() => setShowJoin(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold uppercase tracking-wider rounded-sm">
            <LogIn size={12} /> Join
          </button>
          <button data-testid="create-league-btn" onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm">
            <Plus size={12} /> Create
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-slate-500" />
        </div>
      ) : leagues.length === 0 ? (
        <div className="bg-[#111827] border border-white/5 rounded-sm p-12 text-center" data-testid="no-leagues">
          <Users size={24} className="mx-auto text-slate-600 mb-3" />
          <p className="text-xs text-slate-500 mb-1">No leagues yet.</p>
          <p className="text-[10px] text-slate-600">Create one or join with a code to compete with friends!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {leagues.map(league => (
            <div key={league.id} className="bg-[#111827] border border-white/5 rounded-sm p-4 cursor-pointer hover:border-red-500/20 transition-colors"
              onClick={() => openDetail(league)} data-testid={`league-card-${league.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-red-500" />
                  <span className="text-sm font-bold text-white">{league.name}</span>
                  {league.created_by === userId && <Badge className="text-[10px] bg-amber-500/10 text-amber-400 border-amber-500/30"><Crown size={8} className="mr-1" />Admin</Badge>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500"><Users size={10} className="inline mr-1" />{league.members.length}/{league.max_members}</span>
                  <span className="text-xs font-mono text-red-400 bg-red-500/10 px-2 py-0.5 rounded-sm" data-testid={`league-code-${league.id}`}>{league.code}</span>
                </div>
              </div>
              {league.description && <p className="text-[11px] text-slate-500 mt-1">{league.description}</p>}
            </div>
          ))}
        </div>
      )}

      <CreateModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={refresh} />
      <JoinModal open={showJoin} onClose={() => setShowJoin(false)} onJoined={refresh} />
      {selectedLeague && <DetailModal league={selectedLeague} onClose={() => { setSelectedLeague(null); refresh(); }} userId={userId} />}
    </div>
  );
}

function CreateModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [max, setMax] = useState("20");
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name required"); return; }
    setLoading(true);
    try {
      const league = await apiCreateLeague(name.trim(), desc.trim(), parseInt(max));
      toast.success(`League "${league.name}" created! Code: ${league.code}`);
      setName(""); setDesc(""); onCreated(); onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0f1628] border-white/10 max-w-sm" data-testid="create-league-modal">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "'Rajdhani', sans-serif" }}>CREATE LEAGUE</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Set up a fantasy prediction league.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-3 mt-1">
          <Input data-testid="league-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="League name" className="bg-white/5 border-white/10 h-9 text-xs" maxLength={30} />
          <Input data-testid="league-description-input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)" className="bg-white/5 border-white/10 h-9 text-xs" maxLength={100} />
          <Select value={max} onValueChange={setMax}>
            <SelectTrigger className="bg-white/5 border-white/10 h-9 text-xs" data-testid="league-max-members"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10">
              {[5,10,20,50,100].map(n => <SelectItem key={n} value={String(n)}>{n} members</SelectItem>)}
            </SelectContent>
          </Select>
          <button type="submit" disabled={loading} data-testid="create-league-submit"
            className="w-full py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
            {loading && <Loader2 size={12} className="animate-spin" />}
            Create League
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function JoinModal({ open, onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) { toast.error("Enter a valid 6-character code"); return; }
    setLoading(true);
    try {
      await apiJoinLeague(code.trim());
      toast.success("Joined league!");
      setCode(""); onJoined(); onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0f1628] border-white/10 max-w-sm" data-testid="join-league-modal">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: "'Rajdhani', sans-serif" }}>JOIN LEAGUE</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Enter the 6-character code.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleJoin} className="space-y-3 mt-1">
          <Input data-testid="join-code-input" value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="ABCD12" className="bg-white/5 border-white/10 text-center text-xl font-mono tracking-[0.4em] uppercase h-12" maxLength={6} autoFocus />
          <button type="submit" disabled={loading} data-testid="join-league-submit"
            className="w-full py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
            {loading && <Loader2 size={12} className="animate-spin" />}
            Join League
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DetailModal({ league, onClose, userId }) {
  const isAdmin = league.created_by === userId;
  const sorted = [...league.members].sort((a, b) => b.points - a.points);

  const handleCopy = () => { navigator.clipboard.writeText(league.code); toast.success("Code copied!"); };

  const handleLeave = async () => {
    try {
      await apiLeaveLeague(league.id);
      toast.success(isAdmin ? "League deleted" : "Left league");
      onClose();
    } catch (err) { toast.error(err.message); }
  };

  const handleRemove = async (memberId) => {
    try {
      await apiRemoveMember(league.id, memberId);
      toast.success("Member removed");
      onClose();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="bg-[#0f1628] border-white/10 max-w-md" data-testid="league-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            <Shield size={18} className="text-red-500" /> {league.name}
          </DialogTitle>
          {league.description && <DialogDescription className="text-xs">{league.description}</DialogDescription>}
        </DialogHeader>
        <div className="flex items-center justify-between py-2 border-y border-white/5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500">Code:</span>
            <span className="font-mono font-bold text-red-400 tracking-[0.2em] cursor-pointer" onClick={handleCopy} data-testid="league-detail-code">{league.code}</span>
            <button onClick={handleCopy} className="text-slate-500 hover:text-white" data-testid="copy-code-btn"><Copy size={12} /></button>
          </div>
          <button data-testid="leave-league-btn" onClick={handleLeave} className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1 font-semibold">
            <LogOut size={10} /> {isAdmin ? "Delete" : "Leave"}
          </button>
        </div>
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">Members ({league.members.length}/{league.max_members})</p>
          <div className="space-y-1">
            {sorted.map((m, i) => (
              <div key={m.user_id} className={`flex items-center gap-2 p-2 rounded-sm ${i === 0 ? "bg-red-500/10 border border-red-500/20" : "bg-white/[0.02]"}`} data-testid={`league-member-${m.user_id}`}>
                <span className={`text-xs font-bold font-mono w-5 text-center ${i === 0 ? "text-red-400" : "text-slate-600"}`}>{i + 1}</span>
                <span className="text-xs text-white flex-1 truncate">{m.username}</span>
                {m.user_id === league.created_by && <Crown size={10} className="text-amber-400" />}
                {m.user_id === userId && <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400">You</Badge>}
                <span className="text-[10px] text-slate-500 font-mono">{m.predictions}p</span>
                <span className="text-xs font-bold font-mono text-red-400">{m.points}</span>
                {isAdmin && m.user_id !== userId && (
                  <button data-testid={`remove-member-${m.user_id}`} onClick={e => { e.stopPropagation(); handleRemove(m.user_id); }} className="p-1 hover:bg-red-500/10 text-slate-500 hover:text-red-400 rounded"><Trash2 size={10} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
